import { GetStaticProps } from 'next';

import { ReferencesRibbon } from '@common/ReferencesRibbon';

import Meta from '@components/Meta';
import { NavBar } from '@components/NavBar/NavBar';
import { Footer } from '@components/Footer/Footer';
import { RecipesSection } from '../components/RecipesAndGuides/RecipesSection';
import { NutritionSection } from '@components/RecipesAndGuides/NutritionSection';
import { BlogSection } from '@components/RecipesAndGuides/BlogSection';
import { LifestyleSection } from '@components/RecipesAndGuides/LifestyleSection';
import { DiabetesSection } from '@components/RecipesAndGuides/DiabetesSection';
import { ExerciseSection } from '@components/RecipesAndGuides/ExerciseSection';
import { MindSection } from '@components/RecipesAndGuides/MindSection';
import { SleepSection } from '@components/RecipesAndGuides/SleepSection';
import { RecipesAndGuidesHeader } from '@components/RecipesAndGuides/RecipesAndGuidesHeader';
import { RecipesAndGuidesMenu } from '@components/RecipesAndGuides/RecipesAndGuidesMenu';

import {
  combinePostsForCountryVariants,
  getGuidesArticles,
  postGuidesQuery,
} from '@nextUtils/guidesUtils';

import {
  ArticleCategoriesType,
  CategoryFieldsFragment,
  RecentArticleEdgeType,
} from '@nextTypes/guides';
import { SignPost } from '@common/SignPost';
import { ErrorTracking } from '@common/errorTracking';

const NUMBER_OF_BLOG_POSTS = 4;
const NUMBER_OF_DIABETES_GUIDES = 3;
const NUMBER_OF_EXERCISE_GUIDES = 3;
const NUMBER_OF_LIFESTYLE_GUIDES = 4;
const NUMBER_OF_MIND_GUIDES = 3;
const NUMBER_OF_NUTRITION_GUIDES = 3;
const NUMBER_OF_RECIPE_GUIDES = 6;
const NUMBER_OF_SLEEP_GUIDES = 3;

type Props = {
  allCategories: ArticleCategoriesType;
  blog: { edges: RecentArticleEdgeType[] };
  diabetes: { edges: RecentArticleEdgeType[] };
  exercise: { edges: RecentArticleEdgeType[] };
  lifestyle: { edges: RecentArticleEdgeType[] };
  mind: { edges: RecentArticleEdgeType[] };
  nutrition: { edges: RecentArticleEdgeType[] };
  recipes: { edges: RecentArticleEdgeType[] };
  sleep: { edges: RecentArticleEdgeType[] };
};

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => {
  try {
    const query = `
      ${CategoryFieldsFragment}
      query {
        allCategories: categories(first: 100, where: {shouldOutputInFlatList: true}) {
          edges {
            node {
              ...CategoryFields
            }
          }
        }
        recipes: posts(first: ${
          NUMBER_OF_RECIPE_GUIDES * 2
        }, where: { categoryName: "Recipes", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        nutrition: posts(first: ${
          NUMBER_OF_NUTRITION_GUIDES * 2
        }, where: { categoryName: "Nutrition", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        lifestyle: posts(first: ${
          NUMBER_OF_LIFESTYLE_GUIDES * 2
        }, where: { categoryName: "Lifestyle", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        diabetes: posts(first: ${
          NUMBER_OF_DIABETES_GUIDES * 2
        }, where: { categoryName: "Diabetes", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        exercise: posts(first: ${
          NUMBER_OF_EXERCISE_GUIDES * 2
        }, where: { categoryName: "Exercise", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        sleep: posts(first: ${
          NUMBER_OF_SLEEP_GUIDES * 2
        }, where: { categoryName: "Sleeping", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        blog: posts(first: ${
          NUMBER_OF_BLOG_POSTS * 2
        }, where: { categoryName: "Blog", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              author {
                name
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
        mind: posts(first: ${
          NUMBER_OF_MIND_GUIDES * 2
        }, where: { categoryName: "Mind", tagSlugIn: [ "all_countries", "${locale}" ] }) {
          edges {
            cursor
            node {
              title
              slug
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              seo {
                metaDesc
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
      }
    `;

    const json = await postGuidesQuery(query);

    return {
      props: {
        allCategories: json.data.allCategories,
        blog: combinePostsForCountryVariants(
          json.data.blog,
          NUMBER_OF_BLOG_POSTS,
        ),
        diabetes: combinePostsForCountryVariants(
          json.data.diabetes,
          NUMBER_OF_DIABETES_GUIDES,
        ),
        exercise: combinePostsForCountryVariants(
          json.data.exercise,
          NUMBER_OF_EXERCISE_GUIDES,
        ),
        lifestyle: combinePostsForCountryVariants(
          json.data.lifestyle,
          NUMBER_OF_LIFESTYLE_GUIDES,
        ),
        mind: combinePostsForCountryVariants(
          json.data.mind,
          NUMBER_OF_MIND_GUIDES,
        ),
        nutrition: combinePostsForCountryVariants(
          json.data.nutrition,
          NUMBER_OF_NUTRITION_GUIDES,
        ),
        recipes: combinePostsForCountryVariants(
          json.data.recipes,
          NUMBER_OF_RECIPE_GUIDES,
        ),
        sleep: combinePostsForCountryVariants(
          json.data.sleep,
          NUMBER_OF_SLEEP_GUIDES,
        ),
      },
      // Refresh this page at most every 300 seconds
      // If a request comes in within 300 seconds, it will serve the existing cached page
      // If a request comes in outside of 300 seconds, it will serve the cached page - but re-fetch the page in the background
      revalidate: 300,
    };
  } catch (err) {
    ErrorTracking.track(err);
    // We do not handle this error, but it is handled by NextJS https://github.com/vercel/next.js/discussions/11180#discussioncomment-2399
    throw err;
  }
};

const Guides = ({
  diabetes,
  exercise,
  lifestyle,
  nutrition,
  recipes,
  sleep,
  blog,
  mind,
}: Props): JSX.Element => {
  const recipeArticles = getGuidesArticles(recipes.edges);
  const nutritionArticles = getGuidesArticles(nutrition.edges);
  const lifestyleArticles = getGuidesArticles(lifestyle.edges);
  const diabetesArticles = getGuidesArticles(diabetes.edges);
  const exerciseArticles = getGuidesArticles(exercise.edges);
  const sleepArticles = getGuidesArticles(sleep.edges);
  const blogArticles = getGuidesArticles(blog.edges);
  const mindArticles = getGuidesArticles(mind.edges);

  return (
    <>
      <Meta
        title="Evidence-Based Health Resources | Second Nature"
        metaDescription="Knowledge is power. Give yourself the tools you need to achieve a healthier lifestyle with Second Nature’s science-based guides."
        relativeCanonicalURL="/guides"
      />
      <NavBar utmMedium="guides" utmCampaign="navbar" />
      <RecipesAndGuidesHeader />
      <RecipesAndGuidesMenu />
      <LifestyleSection articles={lifestyleArticles} />
      <NutritionSection articles={nutritionArticles} />
      <BlogSection articles={blogArticles} />
      <DiabetesSection articles={diabetesArticles} />
      <ExerciseSection articles={exerciseArticles} />
      <RecipesSection articles={recipeArticles} />
      <MindSection articles={mindArticles} />
      <SleepSection articles={sleepArticles} />
      <SignPost version="A" utmMedium="guides" utmCampaign="guides_banner" />
      <ReferencesRibbon />
      <Footer />
    </>
  );
};

export default Guides;
